import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Dialog from "@radix-ui/react-dialog";
import { Plus, Trash2 } from "lucide-react";

import { Assessment, AssessmentsDocument, Question, QuestionType } from "./assessmentsTypes";
import styles from "./CompanyAssessments.module.css";
import {
    addQuestion,
    deleteAssessment,
    deleteQuestion,
    updateAssessment
} from "./assessmentsMutations";
import { MAX_TITLE_LENGTH } from "./assessmentsSchema";
import { QuestionEditorDialog } from "./QuestionEditorDialog";

interface AssessmentEditorModalProps {
    open: boolean;
    assessment: Assessment | null;
    document: AssessmentsDocument;
    onOpenChange: (open: boolean) => void;
    onDraftChange: (document: AssessmentsDocument) => void;
}

export function AssessmentEditorModal({
    open,
    assessment,
    document,
    onOpenChange,
    onDraftChange
}: AssessmentEditorModalProps) {
    const { t } = useTranslation();
    const [titleDraft, setTitleDraft] = useState("");
    const [descriptionDraft, setDescriptionDraft] = useState("");
    const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
    const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);

    const currentAssessment = assessment
        ? document.assessments.find(item => item.id === assessment.id) ?? null
        : null;

    useEffect(() => {
        if (open && currentAssessment) {
            setTitleDraft(currentAssessment.title);
            setDescriptionDraft(currentAssessment.description ?? "");
        }
    }, [open, currentAssessment?.id, currentAssessment?.title, currentAssessment?.description]);

    if (!currentAssessment) {
        return null;
    }

    const handleTitleBlur = () => {
        onDraftChange(updateAssessment(document, currentAssessment.id, { title: titleDraft }));
    };

    const handleDescriptionBlur = () => {
        onDraftChange(
            updateAssessment(document, currentAssessment.id, { description: descriptionDraft })
        );
    };

    const handleDeleteAssessment = () => {
        onDraftChange(deleteAssessment(document, currentAssessment.id));
        onOpenChange(false);
    };

    const handleAddQuestion = (type: QuestionType) => {
        const defaultChoiceSetId = document.choice_sets[0]?.id;
        const { document: next, question } = addQuestion(
            document,
            currentAssessment.id,
            type,
            type === "multiple_choice" ? defaultChoiceSetId : undefined
        );
        onDraftChange(next);
        setSelectedQuestion(question);
        setQuestionDialogOpen(true);
    };

    const handleEditQuestion = (question: Question) => {
        setSelectedQuestion(question);
        setQuestionDialogOpen(true);
    };

    const handleDeleteQuestion = (questionId: string) => {
        onDraftChange(deleteQuestion(document, currentAssessment.id, questionId));
    };

    return (
        <>
            <Dialog.Root open={open} onOpenChange={onOpenChange}>
                <Dialog.Portal>
                    <Dialog.Overlay className={styles.dialogOverlay} />
                    <Dialog.Content className={`${styles.dialogContent} ${styles.dialogContentWide}`}>
                        <Dialog.Title className={styles.dialogTitle}>
                            {t("settingsPage.company.assessments.editAssessment")}
                        </Dialog.Title>

                        <label className={styles.dialogFieldLabel}>
                            {t("settingsPage.company.assessments.assessmentTitle")}
                            <input
                                className={styles.dialogInput}
                                type="text"
                                value={titleDraft}
                                maxLength={MAX_TITLE_LENGTH}
                                onChange={event => setTitleDraft(event.target.value)}
                                onBlur={handleTitleBlur}
                            />
                        </label>

                        <label className={styles.dialogFieldLabel}>
                            {t("settingsPage.company.assessments.assessmentDescription")}
                            <textarea
                                className={styles.dialogTextarea}
                                value={descriptionDraft}
                                onChange={event => setDescriptionDraft(event.target.value)}
                                onBlur={handleDescriptionBlur}
                            />
                        </label>

                        <div className={styles.entryActions}>
                            <button
                                type="button"
                                className={styles.entryButton}
                                onClick={() => handleAddQuestion("scale")}
                            >
                                <Plus size={14} />
                                {t("settingsPage.company.assessments.addScaleQuestion")}
                            </button>
                            <button
                                type="button"
                                className={styles.entryButton}
                                onClick={() => handleAddQuestion("text")}
                            >
                                <Plus size={14} />
                                {t("settingsPage.company.assessments.addTextQuestion")}
                            </button>
                            <button
                                type="button"
                                className={styles.entryButton}
                                onClick={() => handleAddQuestion("multiple_choice")}
                                disabled={document.choice_sets.length === 0}
                            >
                                <Plus size={14} />
                                {t("settingsPage.company.assessments.addMultipleChoiceQuestion")}
                            </button>
                        </div>

                        {currentAssessment.questions.length === 0 ? (
                            <p className={styles.emptyState}>
                                {t("settingsPage.company.assessments.noQuestions")}
                            </p>
                        ) : (
                            <div className={styles.list}>
                                {currentAssessment.questions.map(question => (
                                    <div key={question.id} className={styles.questionRow}>
                                        <button
                                            type="button"
                                            className={styles.listItem}
                                            style={{ flex: 1, border: "none", background: "transparent" }}
                                            onClick={() => handleEditQuestion(question)}
                                        >
                                            <span>{question.prompt.trim() || t("settingsPage.company.assessments.untitledQuestion")}</span>
                                            <span className={styles.questionTypeBadge}>{question.type}</span>
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.dialogDeleteButton}
                                            onClick={() => handleDeleteQuestion(question.id)}
                                            aria-label={t("settingsPage.company.assessments.deleteQuestion")}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className={styles.dialogActions}>
                            <button
                                type="button"
                                className={styles.dialogDeleteButton}
                                onClick={handleDeleteAssessment}
                            >
                                {t("settingsPage.company.assessments.deleteAssessment")}
                            </button>
                            <div className={styles.dialogPrimaryActions}>
                                <Dialog.Close asChild>
                                    <button type="button" className={styles.dialogCancelButton}>
                                        {t("settingsPage.account.cancel")}
                                    </button>
                                </Dialog.Close>
                            </div>
                        </div>
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog.Root>

            <QuestionEditorDialog
                open={questionDialogOpen}
                assessmentId={currentAssessment.id}
                question={selectedQuestion}
                document={document}
                onOpenChange={setQuestionDialogOpen}
                onDraftChange={onDraftChange}
            />
        </>
    );
}
